// btw-front/src/components/admin/NoticeForm.jsx
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";

const MB = 1024 * 1024;
const ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.hwp,.hwpx,.jpg,.jpeg,.png,.zip";

function formatSize(bytes) {
  if (bytes == null) return "";
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

/**
 * 공지 등록/수정 공유 폼.
 *  - form/setForm: { title, content, status, isPinned, isFeatured(, notifyPartners) }
 *  - newFiles/setNewFiles: 새로 업로드할 File[]
 *  - existing(편집): [{ id, originalName, sizeBytes }] + onRemoveExisting / onDownloadExisting
 *  - showNotifyPartners(등록 전용): 파트너 알림 메일 체크박스 노출 여부
 */
export default function NoticeForm({
  form,
  setForm,
  newFiles,
  setNewFiles,
  existing = null,
  onRemoveExisting,
  onDownloadExisting,
  submitting,
  onSubmit,
  cancelTo,
  submitLabel,
  showNotifyPartners = false,
}) {
  const { t } = useTranslation();
  const fileRef = useRef(null);

  function patch(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function pickFiles(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (picked.length) setNewFiles((prev) => [...prev, ...picked]);
  }

  function removeNew(idx) {
    setNewFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mt-6 flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-5 md:p-7"
    >
      <Input
        label={t("admin.notices.form.title")}
        required
        name="title"
        value={form.title}
        placeholder={t("admin.notices.form.titlePlaceholder")}
        onChange={(e) => patch("title", e.target.value)}
        maxLength={200}
      />
      <Textarea
        label={t("admin.notices.form.content")}
        required
        rows={10}
        value={form.content}
        placeholder={t("admin.notices.form.contentPlaceholder")}
        onChange={(e) => patch("content", e.target.value)}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-slate-700">
            {t("admin.notices.form.status")}
          </label>
          <select
            value={form.status}
            onChange={(e) => {
              const status = e.target.value;
              // 발행 해제 시 파트너 알림 체크도 해제 — 비활성 회색 체크가 남아
              // 발송되는 것으로 오해하지 않도록
              setForm((f) => ({
                ...f,
                status,
                ...(showNotifyPartners && status !== "published"
                  ? { notifyPartners: false }
                  : {}),
              }));
            }}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/30"
          >
            <option value="draft">{t("admin.notices.form.statusDraft")}</option>
            <option value="published">{t("admin.notices.form.statusPublished")}</option>
          </select>
        </div>
        <div className="flex flex-col justify-end gap-2 pb-1">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={!!form.isPinned}
              onChange={(e) => patch("isPinned", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent/30"
            />
            {t("admin.notices.form.isPinned")}
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={!!form.isFeatured}
              onChange={(e) => patch("isFeatured", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent/30"
            />
            {t("admin.notices.form.isFeatured")}
          </label>
        </div>
      </div>

      {showNotifyPartners && (
        <div className="flex flex-col gap-1">
          <label
            className={`flex items-center gap-2 text-sm ${
              form.status === "published" ? "text-slate-700" : "text-slate-400"
            }`}
          >
            <input
              type="checkbox"
              checked={!!form.notifyPartners}
              disabled={form.status !== "published"}
              onChange={(e) => patch("notifyPartners", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent/30 disabled:opacity-50"
            />
            {t("admin.notices.form.notifyPartners")}
          </label>
          <p className="pl-6 text-[11px] text-slate-500">
            {t("admin.notices.form.notifyPartnersHint")}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-slate-700">
          {t("admin.notices.form.attachments")}
        </label>

        {existing && existing.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-[11px] text-slate-500">
              {t("admin.notices.form.existingAttachments")}
            </p>
            {existing.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2"
              >
                <button
                  type="button"
                  onClick={() => onDownloadExisting && onDownloadExisting(a)}
                  className="min-w-0 flex-1 truncate text-left text-sm text-accent underline-offset-2 hover:underline"
                  title={a.originalName}
                >
                  {a.originalName}
                </button>
                <span className="shrink-0 text-[11px] text-slate-500">
                  {formatSize(a.sizeBytes)}
                </span>
                <button
                  type="button"
                  onClick={() => onRemoveExisting && onRemoveExisting(a)}
                  className="shrink-0 text-xs font-medium text-rose-600 underline-offset-2 hover:underline"
                >
                  {t("admin.notices.form.remove")}
                </button>
              </div>
            ))}
          </div>
        )}

        {newFiles.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-[11px] text-slate-500">{t("admin.notices.form.newFiles")}</p>
            {newFiles.map((f, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-slate-800" title={f.name}>
                  {f.name}
                </span>
                <span className="shrink-0 text-[11px] text-slate-500">{formatSize(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removeNew(idx)}
                  className="shrink-0 text-xs font-medium text-rose-600 underline-offset-2 hover:underline"
                >
                  {t("admin.notices.form.remove")}
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => fileRef.current && fileRef.current.click()}
          className="flex items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3 text-sm text-slate-600 transition hover:border-accent hover:text-accent"
        >
          {t("admin.notices.form.addFiles")}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={pickFiles}
        />
        <p className="text-[11px] text-slate-500">{t("admin.notices.form.attachmentsHint")}</p>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
        <Link to={cancelTo} className="btw-btn-ghost">
          {t("admin.notices.form.cancel")}
        </Link>
        <button type="submit" disabled={submitting} className="btw-btn-accent disabled:opacity-60">
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
